
import { useState } from 'react';
import { useCreateAdSuggestions } from './useCreateAdSuggestions';
import { useAdSuggestionsData } from './useAdSuggestionsData';

/**
 * Hook principal para gerenciar sugestões de anúncios
 * 
 * Coordena todo o fluxo de sugestões de anúncios, incluindo:
 * - Abertura e fechamento do modal
 * - Criação de novas sugestões
 * - Busca de dados existentes
 * - Controle de estados de loading
 * 
 * Similar ao useAIReport, mas focado em sugestões de anúncios.
 */
export const useAdSuggestions = (targetClinicaId?: string) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: adSuggestions = [], isLoading, refetch } = useAdSuggestionsData(targetClinicaId);
  const createAdSuggestionsMutation = useCreateAdSuggestions();

  // Filtrar sugestões por status
  const pendingSuggestions = adSuggestions.filter(suggestion => 
    suggestion.status === 'pending' || suggestion.status === 'processing'
  );
  
  const completedSuggestions = adSuggestions.filter(suggestion => 
    suggestion.status === 'completed'
  );

  const currentProcessingSuggestion = adSuggestions.find(suggestion => 
    suggestion.status === 'processing'
  );

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const createSuggestions = async (params: {
    clinicaId: string;
    startDate: string;
    endDate: string;
    deliveryMethod: 'system' | 'whatsapp';
    phoneNumber?: string;
  }) => {
    try {
      await createAdSuggestionsMutation.mutateAsync(params);
      closeModal();
      refetch(); // Atualizar lista após criar
    } catch (error) {
      console.error('Erro ao criar sugestões:', error);
    }
  };

  return {
    // Estados do modal
    isModalOpen,
    openModal,
    closeModal,

    // Função principal
    createSuggestions,

    // Estados de loading
    isCreatingSuggestions: createAdSuggestionsMutation.isPending,

    // Dados das sugestões
    adSuggestions,
    pendingSuggestions,
    completedSuggestions,
    currentProcessingSuggestion,

    // Estado de loading da busca
    isLoading,
  };
};
